import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';

export const SAFETY_REPORT_ADVANCED_DETAILS_TITLE = 'Advanced Technical Details';
export const SAFETY_REPORT_ADVANCED_DETAILS_SUBTITLE =
  'Advanced details. Use this when the Safety Scan looks wrong or you need identifier-level diagnostics.';

export const SAFETY_REPORT_LIMITED_EVIDENCE_LABEL = 'Limited — review required';

export type SafetyReportTopStatusPanel = {
  status: string;
  nextStep: string;
  missing: string;
};

export function buildSafetyReportTopStatusPanel(args: {
  reviewComplete: boolean;
  controlStatusLabel: string;
  nextStepLabel: string;
  patternOwnerAssigned: boolean;
  followUpOwnerAssigned: boolean;
  impactTracked: boolean;
  evidenceStrengthLabel: string;
  reportFinalized?: boolean;
}): SafetyReportTopStatusPanel {
  if (args.reportFinalized) {
    return {
      status: 'Signed off',
      nextStep:
        'Pending customer fields are locked. Review the signed artifact, or create a revision / addendum to change walkdown, ownership, or deployment decisions.',
      missing: 'None. This persisted report artifact is already finalized and frozen.',
    };
  }
  const controlStatus = String(args.controlStatusLabel || '').trim();
  const status = controlStatus === 'Report unavailable'
    ? controlStatus
    : !args.reviewComplete
      ? 'Needs review'
      : (controlStatus === 'Control not verified'
        ? 'Review required'
        : controlStatus);
  const missingParts: string[] = [];
  if (!args.patternOwnerAssigned) {
    missingParts.push(displaySafetyManagerLabel('pattern_owner').toLowerCase());
  }
  if (!args.followUpOwnerAssigned) {
    missingParts.push(displaySafetyManagerLabel('followup_owner').toLowerCase());
  }
  if (!args.impactTracked) {
    missingParts.push(displaySafetyManagerLabel('impact_tracking').toLowerCase());
  }
  if (isLowEvidenceStrengthLabel(args.evidenceStrengthLabel)) {
    missingParts.push(`${displaySafetyManagerLabel('evidence_quality').toLowerCase()} confirmation`);
  }
  return {
    status,
    nextStep: args.nextStepLabel,
    missing: missingParts.length > 0 ? missingParts.join(' and ') : 'None identified',
  };
}

export const SAFETY_REPORT_NO_RECURRING_RISKS =
  'No recurring risk patterns in this report scope yet. Adjust the report scope or time window to include active patterns.';

export const SAFETY_REPORT_LOW_EVIDENCE_NOTE =
  'Pattern observations found. Evidence review is required before corrective action is confirmed.';

export const SAFETY_REPORT_EMPTY_EVIDENCE_POINTS =
  'Evidence details for this report scope are still loading or not available yet.';

export function buildPendingCustomerFieldsLockState(args: {
  reportFinalized?: boolean;
  hasActiveRevisionDraft?: boolean;
}): {
  editable: boolean;
  reason: string | null;
  createRevisionLabel: string | null;
} {
  if (!args.reportFinalized) {
    return { editable: true, reason: null, createRevisionLabel: null };
  }
  if (args.hasActiveRevisionDraft) {
    return {
      editable: true,
      reason: 'Editing applies to the open revision draft, not the signed revision bytes.',
      createRevisionLabel: null,
    };
  }
  return {
    editable: false,
    reason:
      'Customer Pending fields are frozen after signature. Create a revision or addendum to continue.',
    createRevisionLabel: 'Create revision / addendum',
  };
}

export function isLowEvidenceStrengthLabel(value: string): boolean {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized.includes('low')
    || normalized.includes('limited')
    || normalized.includes('limitations')
    || normalized.includes('pending')
    || normalized.includes('requires review')
    || normalized.includes('not ready')
  );
}

export function buildEvidenceStrengthMeaning(args: {
  strengthLabel: string;
  detectionBasisLabel?: string | null;
}): string {
  const strength = String(args.strengthLabel || '').trim() || 'Not assessed';
  const basis = String(args.detectionBasisLabel || '').trim();
  const low = isLowEvidenceStrengthLabel(strength);
  const basisClause = basis
    ? ` ${displaySafetyManagerLabel('measurement_basis')}: ${basis}.`
    : '';
  if (low) {
    return `The system captured camera observations for this recurring risk pattern, but review is required before treating it as confirmed.${basisClause}`;
  }
  return `Available camera observations and evidence files support this recurring risk pattern summary.${basisClause}`;
}
